import {
	Avatar,
	BasicButton,
	Loading,
	PromoCode,
	RadioSubscription,
	Typography,
} from "@/components";
import { BackAppBar } from "@/components/ui";
import useProfile from "@/hooks/useProfile";
import { SUBSCRIPTION_STATUS } from "@/libraries/constant";
import {
	applyPromotion,
	fetchPrePaymentData,
	proceedPayment,
	selectProduct,
} from "@/redux/reducers/payment";
import AssistantRoundedIcon from "@mui/icons-material/AssistantRounded";
import CasesRoundedIcon from "@mui/icons-material/CasesRounded";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import NewspaperRoundedIcon from "@mui/icons-material/NewspaperRounded";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { createFileRoute } from "@tanstack/react-router";
import { capitalize } from "lodash";
import upperFirst from "lodash/upperFirst";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { z } from "zod";

export const Route = createFileRoute("/subscription/$plan")({
	params: {
		parse: (params) => ({
			plan: z.nativeEnum(SUBSCRIPTION_STATUS).parse(params.plan),
		}),
		stringify: ({ plan }) => ({ plan: plan.toString() }),
	},
	component: RouteComponent,
});

function RouteComponent() {
	const selectedPlan = Route.useParams({
		select: (state) => state.plan,
	});

	const { profile } = useProfile();
	const [isLoading, setLoading] = useState(false);
	const [promoStatus, setPromoStatus] = useState(false);

	const dispatch = useDispatch();

	const promo = useSelector((state: any) => state.payment?.promotion);
	const paymenStat = useSelector(
		(state: any) => state.payment?.prePayment?.data,
	);

	const {
		control,
		watch,
		setValue,
		getValues,
		handleSubmit,
		formState: { isSubmitting },
	} = useForm({
		mode: "all",
		defaultValues: {
			productId: "",
			promoCode: "",
		},
	});

	const { productId } = watch();

	const paymentSelected = useMemo(
		() =>
			paymenStat?.data?.find(
				(subscription: { stripe_product_id: string }) =>
					subscription.stripe_product_id === productId,
			),
		[productId, paymenStat?.data],
	);

	const handlePromo = useCallback(() => {
		setLoading(true);
		dispatch(
			applyPromotion({
				code: getValues("promoCode"),
				callback: () => {
					setPromoStatus(true);
					setLoading(false);
					dispatch(
						fetchPrePaymentData({
							tier: selectedPlan,
						}),
					);
				},
			}),
		);
		if (promo?.status !== "success") {
			setLoading(false);
		}
	}, [dispatch, promo?.status, selectedPlan, getValues]);

	const totalAmount = useMemo(
		() =>
			[paymenStat?.symbol_currency || "RM", paymenStat?.total_amount || 0].join(
				"",
			),
		[paymenStat?.symbol_currency, paymenStat?.total_amount],
	);

	const handleClear = useCallback(() => {
		setPromoStatus(false);
	}, []);

	const onSubmit: SubmitHandler<Record<"productId" | "promoCode", string>> = (
		_,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		setLoading(true);
		dispatch(
			proceedPayment({
				callback: (link: string) => {
					setLoading(false);
					window.location.href = link;
				},
			}),
		);
	};

	const handleChange = useCallback(
		(subscription: any) => {
			setLoading(true);
			dispatch(
				selectProduct({
					product_id: subscription,
					callback: () => {
						setValue("productId", subscription);
						if (promoStatus) {
							handlePromo();
						}
						dispatch(
							fetchPrePaymentData({
								tier: selectedPlan,
							}),
						);
						setLoading(false);
					},
				}),
			);
		},
		[dispatch, handlePromo, promoStatus, selectedPlan, setValue],
	);

	useEffect(() => {
		// pre action payment
		dispatch(
			fetchPrePaymentData({
				tier: selectedPlan,
			}),
		);
	}, [dispatch, selectedPlan]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!productId) {
			const existingPlan = paymenStat?.data?.find(
				(p: any) =>
					p.name === profile?.subscription_plan?.name &&
					p.tier === profile?.subscription_status,
			);
			const stripeProductId =
				existingPlan?.stripe_product_id ??
				paymenStat?.data?.[1]?.stripe_product_id;
			if (stripeProductId) handleChange(stripeProductId);
		}
	}, [
		selectedPlan,
		paymenStat?.data?.[1]?.stripe_product_id,
		profile?.subscription_plan,
	]);

	return (
		<>
			<BackAppBar title={`${capitalize(selectedPlan)} Plan`} />
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="bg-gray-light flex flex-col gap-2 justify-center items-center"
			>
				<div className="container bg-white py-2">
					<List dense className="!p-0">
						<ListItem className="!px-0 !py-2">
							<ListItemAvatar className="!min-w-16">
								<Avatar className="!bg-primary-light !w-12 !h-12">
									<CasesRoundedIcon className="text-primary" />
								</Avatar>
							</ListItemAvatar>
							<ListItemText
								primary={
									selectedPlan === SUBSCRIPTION_STATUS.PLUS
										? "Up to 30 new cases per month"
										: selectedPlan === SUBSCRIPTION_STATUS.PRO
											? "Unlimited cases per month"
											: "Up to 5 new cases per month"
								}
								secondary={
									selectedPlan === SUBSCRIPTION_STATUS.PRO
										? "No restrictions on case quota"
										: "Quota replenished monthly"
								}
								classes={{
									primary: "!font-medium !text-sm",
									secondary: "!font-medium !text-xs",
								}}
							/>
						</ListItem>
						<ListItem className="!px-0 !py-2">
							<ListItemAvatar className="!min-w-16">
								<Avatar className="!bg-primary-light !w-12 !h-12">
									<NewspaperRoundedIcon className="text-primary" />
								</Avatar>
							</ListItemAvatar>
							<ListItemText
								primary={
									selectedPlan === SUBSCRIPTION_STATUS.PLUS
										? "Follow all bulletin channels"
										: selectedPlan === SUBSCRIPTION_STATUS.PRO
											? "Follow and publish bulletins"
											: "Follow up to 3 channels"
								}
								secondary={
									selectedPlan === SUBSCRIPTION_STATUS.PLUS
										? "Access all medical bulletins"
										: selectedPlan === SUBSCRIPTION_STATUS.PRO
											? "Access and share medical updates"
											: "Stay updated with medical bulletins"
								}
								classes={{
									primary: "!font-medium !text-sm",
									secondary: "!font-medium !text-xs",
								}}
							/>
						</ListItem>
						<ListItem className="!px-0 !py-2">
							<ListItemAvatar className="!min-w-16">
								<Avatar className="!bg-primary-light !w-12 !h-12">
									<GroupsRoundedIcon className="text-primary" />
								</Avatar>
							</ListItemAvatar>
							<ListItemText
								primary={
									selectedPlan === SUBSCRIPTION_STATUS.Free
										? "Access to personal directory listing"
										: "Access Pro & Verified directory listings"
								}
								secondary={
									selectedPlan === SUBSCRIPTION_STATUS.Free
										? "Basic Professional Medical Directory access"
										: "Full Professional Medical Directory access"
								}
								classes={{
									primary: "!font-medium !text-sm",
									secondary: "!font-medium !text-xs",
								}}
							/>
						</ListItem>
						<ListItem className="!px-0 !py-2">
							<ListItemAvatar className="!min-w-16">
								<Avatar className="!bg-primary-light !w-12 !h-12">
									<AssistantRoundedIcon className="text-primary" />
								</Avatar>
							</ListItemAvatar>
							<ListItemText
								primary={
									<span>
										<Typography
											component="span"
											fontWeight={500}
											fontSize={10}
											className="text-amber-600 p-1 rounded-md bg-amber-100"
										>
											Coming Soon
										</Typography>
										<p>Enjoy Clinical Practice AI Assistant</p>
									</span>
								}
								secondary={`${selectedPlan === SUBSCRIPTION_STATUS.Free ? "Up to  10" : selectedPlan === SUBSCRIPTION_STATUS.PLUS ? "Up to 50" : "Unlimited"} AI questions per month`}
								classes={{
									primary: "!font-medium !text-sm",
									secondary: "!font-medium !text-xs",
								}}
							/>
						</ListItem>
						{/* <ListItem className="!px-0 !py-2">
							<ListItemAvatar className="!min-w-16">
								<Avatar className="!bg-primary-light !w-12 !h-12">
									<SupportAgentRoundedIcon className="text-primary" />
								</Avatar>
							</ListItemAvatar>
							<ListItemText
								primary="Priority customer support"
								secondary="Faster response time for your queries"
								classes={{
									primary: "!font-medium !text-sm",
									secondary: "!font-medium !text-xs",
								}}
							/>
						</ListItem> */}
					</List>
					<div className="pt-3">
						<Accordion variant="outlined" className="!bg-zinc-100 !rounded-xl">
							<AccordionSummary
								expandIcon={<ExpandMoreIcon className="!text-zinc-950" />}
								aria-controls="panel1-content"
								id="panel1-header"
								className="!h-12 !min-h-0"
							>
								<Typography component="span" fontWeight={500} fontSize={14}>
									Your Essential {upperFirst(selectedPlan)} Features
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<List dense={true} className="!py-0">
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary="Connect through unlimited chats"
											className="font-normal text-sm"
										/>
									</ListItem>
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary="Create unlimited teams"
											className="font-normal text-sm"
										/>
									</ListItem>
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary="Stay organized with built-in to-do lists"
											className="font-normal text-sm"
										/>
									</ListItem>
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary="Never miss updates with smart reminders"
											className="font-normal text-sm"
										/>
									</ListItem>
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary="Boost your visibility with professional medical directory listing"
											className="font-normal text-sm"
										/>
									</ListItem>
								</List>
							</AccordionDetails>
						</Accordion>
					</div>
				</div>
				<div className="container bg-white py-2 flex flex-col gap-3 justify-center items-start">
					<Typography fontWeight={700} fontSize={14}>
						Select a Subscription
					</Typography>
					<Controller
						control={control}
						name="productId"
						render={({ field }) => (
							<div className="w-full flex flex-col-reverse gap-3 justify-center items-start">
								{paymenStat?.data?.map((subscription: any) => (
									<RadioSubscription
										key={subscription.stripe_product_id}
										name="subscription"
										value={subscription.stripe_product_id}
										checked={field.value === subscription.stripe_product_id}
										onChange={() =>
											handleChange(subscription.stripe_product_id)
										}
									>
										<h3 className="text-dark text-2xl font-bold">
											{subscription.description}
										</h3>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												fontSize: "10px",
											}}
										>
											{subscription.short_description && (
												<p className="text-gray-100 text-xs font-medium">
													{subscription.short_description}
												</p>
											)}
											{subscription.saved_to && (
												<button
													type="button"
													className="bg-primary text-white rounded py-0.5 px-1"
												>
													Save {subscription.saved_to}
												</button>
											)}
										</Box>
									</RadioSubscription>
								))}
							</div>
						)}
					/>
				</div>
				<div className="container bg-white py-2 flex flex-col gap-3 justify-center items-start">
					<Typography fontWeight={700} fontSize={14}>
						Have a Promo Code?
					</Typography>
					<Controller
						control={control}
						name="promoCode"
						render={({ field }) => (
							<PromoCode
								value={field.value}
								onChange={(e) => field.onChange(e.target.value)}
								onClear={handleClear}
								onSubmit={handlePromo}
								disable={field.disabled}
							/>
						)}
					/>
				</div>
				<div className="container bg-white py-2 flex flex-col gap-3 justify-center items-start">
					<p className="text-dark text-sm font-bold py-2">Summary</p>
					<Box
						sx={{
							width: "100%",
							display: "flex",
							flexDirection: "column",
							gap: 1,
							mb: 1,
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<p className="text-gray-100 text-xs">
								{`${capitalize(selectedPlan)} Plan`} -{" "}
								{paymentSelected?.name ?? "Monthly"} Billing
							</p>
							<p className="text-dark text-xs font-medium">
								{paymenStat?.symbol_currency || "RM"}
								{paymentSelected?.amount || 0}
							</p>
						</Box>

						{!!paymenStat?.tax_amount && (
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<p className="text-gray-100 text-xs">{paymenStat.tax_name}</p>
								<p className="text-dark text-xs font-medium">
									{paymenStat?.symbol_currency || "RM"}
									{paymenStat.tax_amount}
								</p>
							</Box>
						)}
						{promoStatus && (
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<p className="text-gray-100 text-xs">
									Promo Code - {promo.data?.data?.promotion?.name}
								</p>
								<p className="text-danger-300 text-xs font-medium">
									-{paymenStat?.symbol_currency || "RM"}
									{promo.data?.data?.promotion?.amount}
								</p>
							</Box>
						)}
						<Box className="flex justify-between border-solid-top items-center pt-2 mb-4">
							<p className="text-gray-100 text-">Total</p>
							<p className="text-dark text-sm font-medium">{totalAmount}</p>
						</Box>
					</Box>
				</div>
				<div className="container bg-white py-2 flex flex-col justify-center items-center">
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						disable={!productId}
						className="w-full !h-12 !text-sm font-semibold !shadow-none"
					>
						Continue - {totalAmount}
					</BasicButton>
				</div>
			</form>
			<Loading status={isLoading || isSubmitting} />
		</>
	);
}
